import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQueryWithReauth } from "../baseQueryWithReauth";

export const PDF_FORM_TAG = "PDF_FORM";

export interface IPdfFormResponse {
  formId: number;
  externalId: string;
  filename: string;
}

export interface IUpdatePdfFormRequest {
  formId: number;
  file: File;
}

/**
 * PDF form bytes from the dataforms service:
 *   GET  {REACT_APP_YAPI_DATAFORMS}api/v1/pdfForms/{formId}  -> Blob
 *   PUT  {REACT_APP_YAPI_DATAFORMS}api/v1/pdfForms/{formId}  (multipart file)
 * The base query is created with withJsonContentType=false so the browser sets
 * the multipart boundary for the PUT upload (and GET stays header-clean).
 */
export const pdfFormsApi = createApi({
  reducerPath: "pdfFormsApi",
  tagTypes: [PDF_FORM_TAG],
  baseQuery: baseQueryWithReauth(
    `${process.env.REACT_APP_YAPI_DATAFORMS}api/v1`,
    false
  ),
  endpoints: (build) => ({
    getPdfForm: build.query<Blob, number>({
      query: (formId) => ({
        url: `/pdfForms/${formId}`,
        responseHandler: (response) => response.blob(),
        cache: "no-cache",
      }),
      providesTags: (_result, _error, formId) => [
        { type: PDF_FORM_TAG, id: formId },
      ],
    }),
    updatePdfForm: build.mutation<IPdfFormResponse, IUpdatePdfFormRequest>({
      query: ({ formId, file }) => {
        const body = new FormData();
        body.append("file", file, file.name);
        return {
          url: `/pdfForms/${formId}`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: (_result, _error, { formId }) => [
        { type: PDF_FORM_TAG, id: formId },
      ],
    }),
  }),
});

export const { useGetPdfFormQuery, useUpdatePdfFormMutation } = pdfFormsApi;
